import React from "react";
import { useSelector } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";
import { Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, Slider, CircularProgress, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Bolt, EvStation, WaterDrop, PowerSettingsNew, Whatshot, AcUnit, BatteryFull, Battery80, Battery50, Battery20, History, ChevronRight, Euro } from "@mui/icons-material";
import { IconButton, Tooltip, Box } from "@mui/material";
import { ZoomIn, ZoomOut, GridView, ViewList } from "@mui/icons-material";
import { useMemo, useEffect, useRef, useState } from "react";
import { useMediaQuery, useTheme } from "@mui/material";
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { store } from "../store";
import { postData } from "../API/Post";
import styles from '../styles/components/ResidenceDashboard.module.css';
import "../styles/animations.css";

function SortableZonePill({ zone, isActive, onClick }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: zone.id });
  const zoneColor = zone.color || "#c4a574";

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 10 : "auto",
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 2,
        py: 1,
        borderRadius: "999px",
        cursor: isDragging ? "grabbing" : "grab",
        transition: "background-color 0.2s ease, border-color 0.2s ease",
        backgroundColor: isActive ? zoneColor : "var(--bg-card, transparent)",
        border: isActive ? `1px solid ${zoneColor}` : "1px solid var(--border-light)",
        touchAction: "none",
        userSelect: "none",
        "&:hover": {
          backgroundColor: isActive ? zoneColor : "var(--bg-hover)",
        },
      }}
    >
      {!isActive && (
        <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: zoneColor }} />
      )}
      <Typography sx={{
        fontSize: "0.75rem",
        fontWeight: isActive ? 700 : 600,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        color: isActive ? "#ffffff" : "var(--text-secondary)",
      }}>
        {zone.name}
      </Typography>
      <Box sx={{
        backgroundColor: isActive ? "rgba(255,255,255,0.25)" : "var(--badge-bg)",
        borderRadius: "999px",
        px: 0.75,
        py: 0.125,
        minWidth: 20,
        textAlign: "center",
      }}>
        <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: isActive ? "#ffffff" : "var(--text-secondary)" }}>
          {zone.count}
        </Typography>
      </Box>
    </Box>
  );
}

export default function ResidenceDashboard() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { getAccessTokenSilently } = useAuth0();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm")); // < 600px - pour masquer certains éléments
  
  const selectedEstablishment = useSelector((state) => state.selectedEstablishment);
  // Persister établissement sélectionné même après refresh
  const [localEstablishment, setLocalEstablishment] = useState(() => {
    const saved = localStorage.getItem("selectedEstablishment");
    return saved ? JSON.parse(saved) : null;
  });
  // Contrôle de taille des cartes
  const [cardSize, setCardSize] = useState(() => {
    const savedSize = localStorage.getItem("dashboardCardSize");
    return savedSize ? parseInt(savedSize) : 240;
  });

  // Mode d'affichage: 'cards' ou 'list'
  const [viewMode, setViewMode] = useState(() => {
    const savedMode = localStorage.getItem("dashboardViewMode");
    return savedMode || 'cards';
  });

  // Tri custom par consommation en vue liste : 'water' | 'electric' | null.
  // null = tri par défaut (alertes d'abord + alphanum).
  const [listSortBy, setListSortBy] = useState(() => {
    const saved = localStorage.getItem("dashboardListSortBy");
    return saved === 'water' || saved === 'electric' ? saved : null;
  });

  const toggleListSort = (field) => {
    setListSortBy((prev) => {
      const next = prev === field ? null : field;
      if (next) localStorage.setItem("dashboardListSortBy", next);
      else localStorage.removeItem("dashboardListSortBy");
      return next;
    });
  };

  // État pour les relais ON/OFF
  const [relayStates, setRelayStates] = useState({});
  const [mclimateRelayState, setMclimateRelayState] = useState(false); // État MClimate depuis API

  // États pour les vannes thermo
  const [vanneThermoDialog, setVanneThermoDialog] = useState({
    open: false,
    type: null, // 'temperature' | 'mode'
    deviceData: null,
    residenceId: null,
    currentValue: 22,
    newValue: 22,
  });
  const [vanneThermoLoading, setVanneThermoLoading] = useState(false);
  const [vanneThermoSuccess, setVanneThermoSuccess] = useState(false);
  // État local pour les valeurs TRV après commande (mise à jour optimiste)
  const [trvLocalOverrides, setTrvLocalOverrides] = useState({});

  // Récupérer l'état initial du relais au chargement
  useEffect(() => {
    const fetchRelayStatus = async () => {
      try {
        const token = await getAccessTokenSilently();
        const dev_eui = '70b3d52ddd000fb9'; // Device MClimate
        
        const response = await fetch(`/api/relay/${dev_eui}/status`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.relayState) {
            // Mettre à jour l'état global du relais MClimate
            setMclimateRelayState(data.relayState === 'ON');
          }
        }
      } catch (error) {
        // Erreur silencieuse - l'état relais n'est pas critique
      }
    };
    
    fetchRelayStatus();
  }, [getAccessTokenSilently]);


  // Tailles disponibles
  const CARD_SIZES = [
    { value: 200, label: "Petit" },
    { value: 240, label: "Moyen" },
    { value: 280, label: "Large" },
    { value: 320, label: "Très large" }
  ];

  // Fonctions pour ajuster la taille
  const increaseSize = () => {
    const currentIndex = CARD_SIZES.findIndex(size => size.value === cardSize);
    if (currentIndex < CARD_SIZES.length - 1) {
      const newSize = CARD_SIZES[currentIndex + 1].value;
      setCardSize(newSize);
      localStorage.setItem("dashboardCardSize", newSize);
      document.documentElement.style.setProperty('--card-size', `${newSize}px`);
    }
  };

  const decreaseSize = () => {
    const currentIndex = CARD_SIZES.findIndex(size => size.value === cardSize);
    if (currentIndex > 0) {
      const newSize = CARD_SIZES[currentIndex - 1].value;
      setCardSize(newSize);
      localStorage.setItem("dashboardCardSize", newSize);
      document.documentElement.style.setProperty('--card-size', `${newSize}px`);
    }
  };

  // Toggle mode d'affichage
  const toggleViewMode = () => {
    const newMode = viewMode === 'cards' ? 'list' : 'cards';
    setViewMode(newMode);
    localStorage.setItem("dashboardViewMode", newMode);
  };

  const residences = useSelector((state) => state.residences);
  const screens = useSelector((state) => state.screens);
  const establishmentsFromStore = useSelector((state) => state.establishments || []);
  const zones = useSelector((state) => state.zones);
  const selectedZone = useSelector((state) => state.selectedZone);

  // État local pour la zone sélectionnée (persistance)
  const [localSelectedZone, setLocalSelectedZone] = useState(() => {
    const saved = localStorage.getItem("selectedZone");
    if (!saved) return null;
    const parsed = parseInt(saved);
    // Si c'est un ancien format (nom de zone), on le supprime
    if (isNaN(parsed)) {
      localStorage.removeItem("selectedZone");
      return null;
    }
    return parsed;
  });

  // Cache persistant
  const dataCache = useRef({
    residences: null,
    screens: null,
    establishment: null
  });

  // Sauvegarder l'établissement sélectionné dans localStorage
  useEffect(() => {
    if (selectedEstablishment) {
      const id = typeof selectedEstablishment === 'object' ? selectedEstablishment.id : selectedEstablishment;
      localStorage.setItem("persistedEstablishmentId", id);
      setLocalEstablishment(id);
    }
  }, [selectedEstablishment]);

  // Récupérer le dernier établissement au chargement
  useEffect(() => {
    const lastEstablishment = localStorage.getItem('lastSelectedEstablishment');
    if (lastEstablishment && !selectedEstablishment) {
      const establishmentId = parseInt(lastEstablishment);
      if (!isNaN(establishmentId)) {
        dataCache.current.establishment = establishmentId;
      }
    }
  }, [selectedEstablishment]);

  // Calculer les zones disponibles pour l'établissement sélectionné
  const availableZones = useMemo(() => {
    if (!zones || !residences) return [];

    const currentEstablishment = selectedEstablishment || localEstablishment;
    const currentEstablishmentId = typeof currentEstablishment === 'object'
      ? currentEstablishment?.id
      : currentEstablishment;

    if (!currentEstablishmentId || currentEstablishmentId === "0") {
      return zones;
    }

    // Filtrer les zones qui appartiennent à l'établissement sélectionné
    const filteredZones = zones
      .filter(zone => zone.establishment_id === currentEstablishmentId)
      .slice()
      .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0) || a.id - b.id);

    // Compter le nombre de résidences par zone
    const residencesInEstablishment = residences.filter(r =>
      r.establishment_id === currentEstablishmentId && r.active !== 0
    );

    return filteredZones.map(zone => ({
      ...zone,
      count: residencesInEstablishment.filter(r => r.zone_id === zone.id).length
    }));
  }, [zones, residences, selectedEstablishment, localEstablishment]);

  // Capteurs drag & drop : distance=5px pour ne pas bloquer le clic
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleZonesDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = availableZones.findIndex(z => z.id === active.id);
    const newIndex = availableZones.findIndex(z => z.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(availableZones, oldIndex, newIndex);
    const currentEstablishment = selectedEstablishment || localEstablishment;
    const establishmentId = typeof currentEstablishment === 'object' ? currentEstablishment?.id : currentEstablishment;
    if (!establishmentId) return;

    // Mise à jour optimiste du store Redux
    const previousZones = store.getState().zones;
    const updatedZones = previousZones.map(z => {
      if (z.establishment_id !== establishmentId) return z;
      const newPos = reordered.findIndex(r => r.id === z.id);
      return newPos === -1 ? z : { ...z, display_order: newPos };
    });
    store.dispatch({ type: "zones/update", payload: updatedZones });

    // Persistance backend (rollback si erreur)
    postData("/zones/reorder", {
      establishment_id: establishmentId,
      zone_ids: reordered.map(z => z.id),
    }).then(res => {
      if (!res || (res.status && res.status >= 400) || res.isAxiosError) {
        store.dispatch({ type: "zones/update", payload: previousZones });
      }
    });
  };

  // Handler pour le changement de zone
  const handleZoneChange = (event) => {
    const value = event.target.value === "all" ? null : parseInt(event.target.value);
    store.dispatch({
      type: "selectedZone/update",
      payload: value,
    });
    setLocalSelectedZone(value);
    if (value) {
      localStorage.setItem("selectedZone", String(value));
    } else {
      localStorage.removeItem("selectedZone");
    }
  };

  // Calcule les niveaux d'alerte eau/élec depuis les seuils de la résidence (cascade résidence → établissement)
  const getResidenceAlerts = (screen, deviceData, residence, establishment) => {
    const persons = residence?.current_occupation || 1;

    const waterLiters = screen?.water_liter || 0;
    const yellowWater = (residence?.yellow_water_limit_per_person || establishment?.yellow_water_limit_per_person || 0) * persons;
    const redWater = (residence?.red_water_limit_per_person || establishment?.red_water_limit_per_person || 0) * persons;
    const waterAlert = waterLiters > 0 && redWater > 0 && waterLiters > redWater ? "red"
      : waterLiters > 0 && yellowWater > 0 && waterLiters > yellowWater ? "yellow" : null;

    const hasUC300 = deviceData?.usage_by_type && Object.keys(deviceData.usage_by_type || {}).some(k => k.startsWith("uc300_CH"));
    const hasDragino = deviceData?.usage_by_type && Object.keys(deviceData.usage_by_type || {}).some(k => k.startsWith("dragino_CH"));
    const hasAtimMR4 = deviceData?.usage_by_type && Object.keys(deviceData.usage_by_type || {}).some(k => k.startsWith("atim_mr4_CH"));
    const hasMultiChannelElec = hasUC300 || hasDragino || hasAtimMR4;
    const elecKwh = hasMultiChannelElec
      ? Object.entries(deviceData?.usage_by_type || {}).filter(([k]) => k.startsWith("uc300_CH") || k.startsWith("dragino_CH") || k.startsWith("atim_mr4_CH")).reduce((s, [, v]) => s + (parseFloat(v) || 0), 0)
      : (deviceData?.usage_by_type?.electric || screen?.electricity_kwh || 0);
    const yellowElec = (residence?.yellow_elec_limit_per_person || establishment?.yellow_elec_limit_per_person || 0) * persons;
    const redElec = (residence?.red_elec_limit_per_person || establishment?.red_elec_limit_per_person || 0) * persons;
    const elecAlert = elecKwh > 0 && redElec > 0 && elecKwh > redElec ? "red"
      : elecKwh > 0 && yellowElec > 0 && elecKwh > yellowElec ? "yellow" : null;

    const tempAlert = screen?.temperature_color === "red" ? "red" : screen?.temperature_color === "yellow" ? "yellow" : null;

    return { waterAlert, elecAlert, tempAlert };
  };

  // Mémoriser et filtrer les données
  const { filteredResidences, establishments } = useMemo(() => {
    if (!residences || !Array.isArray(residences)) {
      return { filteredResidences: [], establishments: [] };
    }

    // Mise à jour du cache
    dataCache.current.residences = residences;
    dataCache.current.screens = screens;

    // Extraire les établissements uniques
    const uniqueEstablishments = [...new Map(
      residences
        .filter(r => r.establishment_id)
        .map(r => [r.establishment_id, { id: r.establishment_id, name: r.establishment_name || `Établissement ${r.establishment_id}` }])
    ).values()];

    // Utiliser l'établissement sélectionné ou celui du cache
    const currentEstablishment = selectedEstablishment || localEstablishment || dataCache.current.establishment;

    // Filtrer les résidences par établissement
    let filtered = residences;
    if (currentEstablishment && typeof currentEstablishment === 'number') {
      filtered = residences.filter(r => r.establishment_id === currentEstablishment);
    } else if (currentEstablishment && currentEstablishment.id) {
      filtered = residences.filter(r => r.establishment_id === currentEstablishment.id);
    }

    // Filtrer par zone si une zone est sélectionnée ET existe
    const currentZone = selectedZone || localSelectedZone;
    if (currentZone && currentZone !== "all" && !isNaN(currentZone)) {
      const zoneId = typeof currentZone === 'string' ? parseInt(currentZone) : currentZone;
      // Vérifier que la zone existe dans les résidences filtrées
      const zoneExistsInResidences = filtered.some(r => r.zone_id === zoneId);
      if (!isNaN(zoneId) && zoneId > 0 && zoneExistsInResidences) {
        filtered = filtered.filter(r => r.zone_id === zoneId);
      } else if (zoneId > 0 && !zoneExistsInResidences) {
        // Zone invalide, on la supprime du localStorage
        localStorage.removeItem("selectedZone");
      }
    }

    // Toujours filtrer les résidences actives
    filtered = filtered.filter(residence => residence.active !== 0);

  // Récupère la conso eau (litres) d'un screen pour le tri.
  const getWaterValue = (screen) => {
    if (!screen) return 0;
    const usage = screen.device_data?.usage_by_type || {};
    return parseFloat(usage.water) || parseFloat(screen.water_liter) || 0;
  };

  // Récupère la conso élec (kWh) d'un screen, en sommant les canaux
  // uc300_CH* / dragino_CH* / atim_mr4_CH* s'ils existent, sinon fallback sur electric/cache.
  const getElecValue = (screen) => {
    if (!screen) return 0;
    const usage = screen.device_data?.usage_by_type || {};
    const channelSum = Object.entries(usage)
      .filter(([k]) => k.startsWith('uc300_CH') || k.startsWith('dragino_CH') || k.startsWith('atim_mr4_CH'))
      .reduce((sum, [, v]) => sum + (parseFloat(v) || 0), 0);
    if (channelSum > 0) return channelSum;
    return parseFloat(usage.electric) || parseFloat(screen.electricity_kwh) || 0;
  };

  // Détermine si une résidence a un compteur d'eau.
  const hasWaterMeter = (screen) => {
    if (!screen) return false;
    const usage = screen.device_data?.usage_by_type || {};
    return 'water' in usage || screen.water_liter != null;
  };

  // Détermine si une résidence a un compteur élec (mono ou multicanal).
  const hasElecMeter = (screen) => {
    if (!screen) return false;
    const usage = screen.device_data?.usage_by_type || {};
    const hasChannels = Object.keys(usage).some(k => k.startsWith('uc300_CH') || k.startsWith('dragino_CH') || k.startsWith('atim_mr4_CH'));
    return hasChannels || 'electric' in usage || screen.electricity_kwh != null;
  };

  // En vue liste avec un tri actif : on filtre en plus pour ne garder
  // que les résidences qui ont le type de compteur sélectionné.
  if (viewMode === 'list' && listSortBy) {
    const hasMeter = listSortBy === 'water' ? hasWaterMeter : hasElecMeter;
    filtered = filtered.filter(r => {
      const screen = screens.find(s => s.pack_id === r.pack_id);
      return hasMeter(screen);
    });
  }

  // Tri : en vue liste avec un tri custom actif → par conso descendante ;
  // sinon tri par défaut (alertes d'abord + alphanum).
  filtered.sort((a, b) => {
    const screenA = screens.find(s => s.pack_id === a.pack_id);
    const screenB = screens.find(s => s.pack_id === b.pack_id);

    if (viewMode === 'list' && listSortBy) {
      const getValue = listSortBy === 'water' ? getWaterValue : getElecValue;
      const diff = getValue(screenB) - getValue(screenA); // descendant
      if (diff !== 0) return diff;
      return a.name.localeCompare(b.name, 'fr', { numeric: true, sensitivity: 'base' });
    }

    // Déterminer le niveau d'alerte
    const getSortAlertLevel = (screen, residence) => {
      if (!screen) return 0;
      const estab = establishmentsFromStore.find(e => e.id === residence.establishment_id);
      const { waterAlert, elecAlert, tempAlert } = getResidenceAlerts(screen, screen?.device_data || {}, residence, estab);
      if (waterAlert === 'red' || elecAlert === 'red' || tempAlert === 'red') return 2;
      if (waterAlert === 'yellow' || elecAlert === 'yellow' || tempAlert === 'yellow') return 1;
      return 0;
    };

    const alertA = getSortAlertLevel(screenA, a);
    const alertB = getSortAlertLevel(screenB, b);

    if (alertA !== alertB) {
      return alertB - alertA;
    }

    return a.name.localeCompare(b.name, 'fr', { numeric: true, sensitivity: 'base' });
  });

    return {
      filteredResidences: filtered,
      establishments: uniqueEstablishments
    };
  }, [residences, screens, selectedEstablishment, localEstablishment, selectedZone, localSelectedZone, establishmentsFromStore, viewMode, listSortBy]);

  const currentEstablishmentData = useMemo(() => {
    const currentId = selectedEstablishment?.id || selectedEstablishment;
    // D abord chercher dans le store (données complètes de l API)
    const fromStore = establishmentsFromStore.find(e => e.id === currentId);
    if (fromStore) return fromStore;
    // Sinon utiliser les données extraites des residences
    return establishments.find(e => e.id === currentId) || establishments[0] || {};
  }, [establishments, selectedEstablishment, establishmentsFromStore]);

  const handleCardClick = (residence) => {
    navigate(`/view/${residence.id}`);
  };
  // Gestion du toggle relais - empêche la navigation
  // meta = {brand, channel} : si brand === 'ade_dry', le call est routé vers /api/adedry
  // avec un payload {channel, state} au lieu de /api/relay {setRelayState}.
  const handleRelayToggle = async (e, residenceId, deviceKey, deviceDevEui = null, meta = null) => {
    e.stopPropagation();
    const relayKey = `${residenceId}_${deviceKey}`;
    const newState = !relayStates[relayKey];

    // Mise à jour optimiste de l'UI
    setRelayStates(prev => ({...prev, [relayKey]: newState}));

    try {
      // Récupération du token
      const token = await getAccessTokenSilently();

      // Récupérer dev_eui - soit passé en paramètre, soit recherché
      let dev_eui = null;

      if (deviceDevEui) {
        // dev_eui passé directement - enlever le suffixe _CH pour obtenir le principal
        dev_eui = deviceDevEui.replace(/_CH[0-9]+$/, '');
      } else {
        // Fallback: chercher dans les devices de la résidence
        const devices = residences?.find(r => r.id === residenceId)?.devices || [];
        const device = devices.find(d => d.name === deviceKey || d.dev_eui === deviceKey);
        if (device?.dev_eui) {
          dev_eui = device.dev_eui.replace(/_CH[0-9]+$/, '');
        }
      }

      if (!dev_eui) {
        throw new Error('Device EUI non trouvé');
      }

      // Choix de l'endpoint en fonction de la marque
      let endpoint, body;
      if (meta?.brand === 'ade_dry' && meta?.channel) {
        endpoint = `/api/adedry/${dev_eui}/command`;
        body = { channel: meta.channel, state: newState ? 1 : 0 };
      } else {
        endpoint = `/api/relay/${dev_eui}/command`;
        body = { setRelayState: newState ? 1 : 0 };
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      await response.json();

    } catch (error) {
      // Rollback en cas d'erreur
      setRelayStates(prev => ({...prev, [relayKey]: !newState}));
      alert(`Erreur commande relais: ${error.message}`);
    }
  };

  // Gestion des vannes thermo
  const handleVanneThermoOpenDialog = (e, type, deviceData, residenceId) => {
    e.stopPropagation();
    const currentTemp = deviceData?.heating_temperature || 22;
    setVanneThermoDialog({
      open: true,
      type,
      deviceData,
      residenceId,
      currentValue: currentTemp,
      newValue: currentTemp,
    });
    setVanneThermoSuccess(false);
  };

  const handleVanneThermoCloseDialog = () => {
    setVanneThermoDialog(prev => ({ ...prev, open: false }));
    setVanneThermoSuccess(false);
  };

  const handleVanneThermoCommand = async (command, value) => {
    setVanneThermoLoading(true);
    try {
      const token = await getAccessTokenSilently();

      // Récupérer dev_eui - plusieurs fallbacks
      let dev_eui = vanneThermoDialog.deviceData?.dev_eui;
      const residence = residences?.find(r => r.id === vanneThermoDialog.residenceId);
      const screen = screens?.find(s => s.pack_id === residence?.pack_id);
      const usageByType = screen?.device_data?.usage_by_type || {};

      // Priorité 1: usage_by_type.thermostat.dev_eui ou vanne_thermo.dev_eui (nouveau format backend)
      if (!dev_eui && usageByType.thermostat?.dev_eui) {
        dev_eui = usageByType.thermostat.dev_eui;
      }
      if (!dev_eui && usageByType.vanne_thermo?.dev_eui) {
        dev_eui = usageByType.vanne_thermo.dev_eui;
      }

      // Fallback 2: chercher le device TRV dans la résidence
      if (!dev_eui) {
        const trvDevice = residence?.devices?.find(d => d.device_brand_id === 66 || d.device_type_id === 8);
        dev_eui = trvDevice?.dev_eui;
      }

      // Fallback 3: chercher dans screen.device_data.devices
      if (!dev_eui && screen?.device_data?.devices) {
        const trvFromScreen = screen.device_data.devices.find(d => d.device_brand_id === 66 || d.device_type_id === 8);
        dev_eui = trvFromScreen?.dev_eui;
      }

      // Fallback 4: chercher le dev_eui dans les valeurs de usage_by_type (format objet avec dev_eui)
      if (!dev_eui) {
        for (const key of Object.keys(usageByType)) {
          const data = usageByType[key];
          // Priorité aux clés contenant 'trv', 'vanne', 'thermostat'
          if ((key.includes('trv') || key.includes('vanne') || key === 'vanne_thermo' || key === 'thermostat') && typeof data === 'object' && data.dev_eui) {
            dev_eui = data.dev_eui;
            break;
          }
        }
      }

      // Fallback 5: extraire le dev_eui d'une clé contenant un pattern 16-char hex
      if (!dev_eui) {
        for (const key of Object.keys(usageByType)) {
          if (key.includes('trv') || key.includes('vanne') || key === 'vanne_thermo' || key === 'thermostat') {
            // Pattern: trv_8c1f64e270000068 ou vanne_8c1f64e270000068
            const match = key.match(/[_-]?([a-f0-9]{16})$/i);
            if (match) {
              dev_eui = match[1];
              break;
            }
          }
        }
      }

      // Fallback 6: recherche étendue - chercher dans TOUTES les clés qui contiennent un pattern 16-char hex
      if (!dev_eui) {
        for (const key of Object.keys(usageByType)) {
          const match = key.match(/([a-f0-9]{16})/i);
          if (match) {
            // Vérifier que c'est bien un device type radiateur/thermo
            const data = usageByType[key];
            if (typeof data === 'object' && (data.temperature !== undefined || data.heating_temperature !== undefined || data.valve_position_percent !== undefined)) {
              dev_eui = match[1];
              break;
            }
          }
        }
      }

      // Fallback 7: chercher un dev_eui dans n'importe quel objet de usage_by_type qui a des propriétés TRV
      if (!dev_eui) {
        for (const key of Object.keys(usageByType)) {
          const data = usageByType[key];
          if (typeof data === 'object') {
            // Si c'est un objet avec des propriétés TRV caractéristiques et un dev_eui
            if (data.dev_eui && (data.temperature !== undefined || data.heating_temperature !== undefined || data.valve_position_percent !== undefined)) {
              dev_eui = data.dev_eui;
              break;
            }
          }
        }
      }

      if (!dev_eui) {
        throw new Error('Device EUI non trouvé');
      }

      let endpoint = '';
      let body = null;

      switch (command) {
        case 'set_temperature':
          endpoint = `/api/trv/${dev_eui}/temperature`;
          body = { heating_temp: value };
          break;
        case 'close_valve':
          endpoint = `/api/trv/${dev_eui}/valve/close`;
          break;
        case 'open_valve':
          endpoint = `/api/trv/${dev_eui}/valve/open`;
          break;
        case 'frost_protection':
          endpoint = `/api/trv/${dev_eui}/temperature`;
          body = { heating_temp: 7.5 };
          break;
        case 'auto':
          endpoint = `/api/trv/${dev_eui}/temperature`;
          body = { heating_temp: value || vanneThermoDialog.newValue };
          break;
        default:
          throw new Error('Commande inconnue');
      }

      const fetchOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      };

      if (body) {
        fetchOptions.body = JSON.stringify(body);
      }

      const response = await fetch(endpoint, fetchOptions);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      // Mise à jour optimiste - stocker localement les nouvelles valeurs
      const overrideKey = `${vanneThermoDialog.residenceId}_trv`;
      const currentOverride = trvLocalOverrides[overrideKey] || {};

      let newOverride = { ...currentOverride };
      switch (command) {
        case 'set_temperature':
        case 'auto':
          newOverride.heating_temperature = value || vanneThermoDialog.newValue;
          newOverride.valve_status = 'auto';
          break;
        case 'close_valve':
          newOverride.valve_status = 'closed';
          newOverride.valve_position_percent = 0;
          break;
        case 'open_valve':
          newOverride.valve_status = 'open';
          newOverride.valve_position_percent = 100;
          break;
        case 'frost_protection':
          newOverride.heating_temperature = 7.5;
          newOverride.valve_status = 'frost';
          break;
        default:
          break;
      }

      setTrvLocalOverrides(prev => ({
        ...prev,
        [overrideKey]: newOverride
      }));

      setVanneThermoSuccess(true);
      // Fermer le dialog après 2 secondes
      setTimeout(() => {
        handleVanneThermoCloseDialog();
      }, 2000);

    } catch (error) {
      alert(`Erreur commande vanne: ${error.message}`);
    }
    setVanneThermoLoading(false);
  };

  // Icône batterie pour vanne thermo
  const getVanneBatteryIcon = (battery) => {
    if (battery === null || battery === undefined) return null;
    const batteryNum = parseFloat(battery);
    if (batteryNum <= 20) return <Battery20 style={{ fontSize: '16px', color: '#e74c3c' }} />;
    if (batteryNum <= 50) return <Battery50 style={{ fontSize: '16px', color: '#f39c12' }} />;
    if (batteryNum <= 80) return <Battery80 style={{ fontSize: '16px', color: '#27ae60' }} />;
    return <BatteryFull style={{ fontSize: '16px', color: '#27ae60' }} />;
  };


  // Couleur du liseré d'alerte 3px en haut de carte.
  // En état normal, on renvoie null pour ne pas afficher le liseré du tout
  // (cohérent avec le handoff Paper & Sage — les cartes "ok" sont sans rail).
  const getBorderColor = (waterAlert, elecAlert, tempAlert) => {
    if (waterAlert === 'red' || elecAlert === 'red' || tempAlert === 'red') return '#e04a2b';
    if (waterAlert === 'yellow' || elecAlert === 'yellow' || tempAlert === 'yellow') return '#f0b429';
    return null;
  };

  const renderWaterSection = (screen, deviceData, waterAlert) => {
    const waterValue = screen?.water_liter || 0;
    if (deviceData?.usage_by_type && !("water" in deviceData.usage_by_type)) return null;

    return (
      <div className={`${styles.metricSection} ${styles.waterSection}`} style={{ position: "relative" }}>
        <div className={`${styles.iconWrapper} ${styles.waterIcon}`}>
          <WaterDrop style={{ fontSize: '32px', color: 'var(--color-water)' }} />
        </div>
        <div className={styles.metricContent}>
          <div className={styles.metricValue}>
            {waterValue.toFixed(0)}L
          </div>
          <div className={styles.metricUnit}>
          </div>
          {waterAlert === "red" && <div style={{ position: "absolute", bottom: "8px", right: "8px", fontSize: "10px", fontWeight: 700, letterSpacing: "0.2px", color: "var(--panel-alert-fg)", backgroundColor: "var(--panel-alert-bg)", animation: "pulse 1.5s infinite", padding: "3px 8px", borderRadius: "4px", boxShadow: "0 1px 2px rgba(0,0,0,0.08)" }}>Alert</div>}
          {waterAlert === "yellow" && <div style={{ position: "absolute", bottom: "6px", right: "6px", fontSize: "18px", lineHeight: 1, filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.15))" }}>⚠️</div>}
        </div>
      </div>
    );
  };

  const renderElectricitySection = (screen, deviceData, elecAlert) => {
    // Utiliser usage_by_type (données historiques) au lieu du cache electricity_kwh
    const hasUC300 = deviceData?.usage_by_type && Object.keys(deviceData.usage_by_type).some(key => key.startsWith("uc300_CH"));
    const hasDragino = deviceData?.usage_by_type && Object.keys(deviceData.usage_by_type).some(key => key.startsWith("dragino_CH"));
    const hasAtimMR4 = deviceData?.usage_by_type && Object.keys(deviceData.usage_by_type).some(key => key.startsWith("atim_mr4_CH"));
    const hasMultiChannelElec = hasUC300 || hasDragino || hasAtimMR4;
    const elecValue = hasMultiChannelElec
      ? Object.entries(deviceData.usage_by_type || {})
          .filter(([key]) => key.startsWith("uc300_CH") || key.startsWith("dragino_CH") || key.startsWith("atim_mr4_CH"))
          .reduce((sum, [, value]) => sum + (parseFloat(value) || 0), 0)
      : (deviceData?.usage_by_type?.electric || screen?.electricity_kwh || 0);
    if (!hasMultiChannelElec && deviceData?.usage_by_type && !("electric" in deviceData.usage_by_type)) return null;
    return (
      <div className={`${styles.metricSection} ${styles.electricSection}`} style={{ position: "relative" }}>
        <div className={`${styles.iconWrapper} ${styles.electricIcon}`}>
          {hasUC300 ? <EvStation style={{ fontSize: '32px', color: 'var(--color-electricity)' }} /> : <Bolt style={{ fontSize: '32px', color: 'var(--color-electricity)' }} />}
        </div>
        <div className={styles.metricContent}>
          <div className={styles.metricValue}>
            {elecValue.toFixed(0)}kWh
          </div>
          <div className={styles.metricUnit}>
          </div>
        </div>
        {elecAlert === "red" && <div style={{ position: "absolute", bottom: "8px", right: "8px", fontSize: "10px", fontWeight: 700, letterSpacing: "0.2px", color: "var(--panel-alert-fg)", backgroundColor: "var(--panel-alert-bg)", animation: "pulse 1.5s infinite", padding: "3px 8px", borderRadius: "4px", boxShadow: "0 1px 2px rgba(0,0,0,0.08)" }}>Alert</div>}
        {elecAlert === "yellow" && <div style={{ position: "absolute", bottom: "6px", right: "6px", fontSize: "18px", lineHeight: 1, filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.15))" }}>⚠️</div>}
      </div>
    );
  };

  const renderTemperatureSection = (usage, screen) => {
    const metrics = [];

    // Détection AM103 par device_brand_id (35-38). Sans ça on ne peut pas distinguer
    // un AM103 (CH3 = humidité) d'un SenseCap 3 canaux (CH3 = 3e température, icône flocon).
    const devicesList = screen?.device_data?.devices || [];
    const isAM103 = devicesList.some(d => [35, 36, 37, 38].includes(d.device_brand_id));
    const tempType = isAM103 ? 'temperature_am103' : 'temperature';

    // Température simple - si AM103 et valeur > 100, c'est probablement du CO2
    if (usage?.temperature) {
      if (isAM103 && usage.temperature > 100) {
        // Pour AM103, "temperature" simple est en fait le CO2
        metrics.push({ key: 'temperature', value: usage.temperature, type: 'co2' });
      } else if (!isAM103) {
        // Pas un AM103, c'est une vraie température
        metrics.push({ key: 'temperature', value: usage.temperature, type: tempType });
      }
      // Si isAM103 et <= 100, on ignore car c'est probablement dupliqué avec temperature_CH2
    }

    // Canaux de température (CH1, CH2 = température réelle)
    ['temperature_CH1', 'temperature_CH2'].forEach(key => {
      if (usage?.[key]) {
        metrics.push({ key, value: usage[key], type: tempType });
      }
    });

    // CH3 : AM103 = humidité ; SenseCap 3 canaux = 3e température
    if (usage?.humidity) {
      metrics.push({ key: 'humidity', value: usage.humidity, type: 'humidity' });
    } else if (usage?.temperature_CH3) {
      metrics.push({
        key: 'temperature_CH3',
        value: usage.temperature_CH3,
        type: isAM103 ? 'humidity' : tempType
      });
    }

    // AM103 CH4 = CO2 (données peuvent arriver comme temperature_CH4 ou co2)
    if (usage?.co2) {
      metrics.push({ key: 'co2', value: usage.co2, type: 'co2' });
    } else if (usage?.temperature_CH4) {
      metrics.push({ key: 'temperature_CH4', value: usage.temperature_CH4, type: 'co2' });
    }

    // Configuration par type de métrique
    const getMetricConfig = (type) => {
      switch (type) {
        case 'humidity':
          return { icon: '💧', unit: '%', label: 'Humidité', color: '#00bcd4' };
        case 'co2':
          return { icon: '🌬️', unit: 'ppm', label: 'CO2', color: '#4caf50' };
        case 'temperature_am103':
          // AM103 : thermomètre
          return { icon: '🌡️', unit: '°C', label: '', color: '#4fc3f7' };
        default:
          // Chambres froides et autres : flocon
          return { icon: '❄️', unit: '°C', label: '', color: '#99DCFF' };
      }
    };

    return metrics.map(({ key, value, type }) => {
      const config = getMetricConfig(type);
      const channelLabel = key.includes('CH') ? key.split('_')[1] : config.label;

      return (
        <div key={key} className={`${styles.metricSection} ${styles.tempSection}`}>
          <div className={`${styles.iconWrapper} ${styles.tempIcon}`}>
            <span style={{ fontSize: '32px' }}>{config.icon}</span>
          </div>
          <div className={styles.metricContent}>
            <div className={styles.metricValue}>
              {Math.round(value * 100) / 100}{config.unit}
            </div>
            <div className={styles.metricUnit}>
              <div className={styles.temperatureLabel}>
                {channelLabel}
              </div>
            </div>
          </div>
          {(() => {
            const channelColor = screen?.device_data?.colors?.[key] || "green";
            if (channelColor === "red") {
              return <div style={{ position: "absolute", bottom: "8px", right: "8px", fontSize: "10px", fontWeight: 700, letterSpacing: "0.2px", color: "var(--panel-alert-fg)", backgroundColor: "var(--panel-alert-bg)", animation: "pulse 1.5s infinite", padding: "3px 8px", borderRadius: "4px", boxShadow: "0 1px 2px rgba(0,0,0,0.08)" }}>Alert</div>;
            }
            if (channelColor === "yellow") {
              return <div style={{ position: "absolute", bottom: "6px", right: "6px", fontSize: "18px", lineHeight: 1, filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.15))" }}>⚠️</div>;
            }
            return null;
          })()}
        </div>
      );
    });
  };

  // Render section pour les relais pilotables avec consommation
  const renderRelaySection = (usage, screen, residence, skipAdeDry = false) => {
    let relayData = [];

    if (usage) {
      Object.keys(usage).forEach(key => {
        // Méthode 1: Détecter par préfixe classique (relay_, smart_plug_)
        if (key.startsWith('relay_') || key.startsWith('smart_plug_')) {
          relayData.push({ key, consumption: usage[key] || 0 });
          return;
        }

        // Méthode 2: Détecter par type 'switch' (device_type_id = 5) - MClimate Relay
        // Le backend retourne: switch: {relay_state: 1, dev_eui: '70b3d52ddd000fb9'}
        // relay_state = 1 ou 'ON' signifie ON, 0 ou null = OFF
        if (key === 'switch') {
          const switchData = usage[key];
          // Vérifier si c'est le nouveau format (object avec relay_state et dev_eui)
          if (typeof switchData === 'object' && switchData.dev_eui) {
            // relay_state peut être: 1, 'ON', true, 1.0
            const relayState = switchData.relay_state;
            const isRelayOn = relayState === 1 || relayState === 'ON' || relayState === true || relayState === 1.0;
            relayData.push({
              key,
              consumption: 0, // La consommation est maintenant sur CH2 (electric)
              dev_eui: switchData.dev_eui,
              backendState: isRelayOn
            });
          } else {
            // Ancien format (juste un nombre)
            relayData.push({ key, consumption: switchData || 0, backendState: switchData >= 1 });
          }
          return;
        }

        // Méthode 3: ADEUNIS Dry Contacts - 4 canaux indépendants (clés ade_dry_A..D)
        // Backend: usage[ade_dry_A] = {dev_eui, relay_state, channel: 'A', brand: 'ade_dry'}
        if (key.startsWith('ade_dry_')) {
          if (skipAdeDry) return;
          const chData = usage[key];
          if (typeof chData === 'object' && chData.dev_eui) {
            const relayState = chData.relay_state;
            const isRelayOn = relayState === 1 || relayState === 'ON' || relayState === true || relayState === 1.0;
            relayData.push({
              key,
              consumption: 0,
              dev_eui: chData.dev_eui,
              backendState: isRelayOn,
              meta: { brand: 'ade_dry', channel: chData.channel },
              label: `Canal ${chData.channel}`,
            });
          }
        }
      });
    }

    if (relayData.length === 0) return null;

    return relayData.map(({ key, consumption, dev_eui, backendState, meta, label }) => {
      const relayKey = `${residence.id}_${key}`;
      // Priorité: state local (après clic) > état backend (données CRON)
      const isOn = relayStates[relayKey] !== undefined ? relayStates[relayKey] : (backendState !== undefined ? backendState : mclimateRelayState);
      
      return (
        <React.Fragment key={key}>
          <div 
            className={`${styles.metricSection} ${styles.relayControlSection}`}
            style={{ 
              borderTopColor: isOn ? '#27ae60' : '#c0392b',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '8px'
            }}
            onClick={(e) => handleRelayToggle(e, residence.id, key, dev_eui, meta)}
          >
            {/* Bouton Power rond avec texte ON/OFF - vertical desktop, horizontal mobile */}
            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'row' : 'column',
              alignItems: 'center',
              gap: isMobile ? '10px' : '6px'
            }}>
              <div style={{
                width: isMobile ? '40px' : '45px',
                height: isMobile ? '40px' : '45px',
                borderRadius: '50%',
                backgroundColor: isOn ? '#27ae60' : '#e74c3c',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: isOn
                  ? '0 4px 15px rgba(39, 174, 96, 0.4)'
                  : '0 4px 15px rgba(231, 76, 60, 0.4)'
              }}>
                <PowerSettingsNew
                  style={{ fontSize: isMobile ? '22px' : '26px', color: 'white' }}
                />
              </div>
              {label && (
                <span style={{
                  color: 'var(--color-text-primary, #2c3e50)',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  marginTop: '2px',
                }}>
                  {label}
                </span>
              )}
              <span style={{
                color: isOn ? '#27ae60' : '#e74c3c',
                fontSize: '1.1rem',
                fontWeight: 'bold'
              }}>
                {isOn ? 'ON' : 'OFF'}
              </span>
            </div>
          </div>

          {/* Carte consommation - affichée seulement pour relay/smart_plug, pas pour switch */}
          {/* Les switches MClimate ont leur consommation sur CH2 (electric) */}
          {consumption > 0 && key !== 'switch' && (
            <div 
              className={`${styles.metricSection} ${styles.electricSection}`}
              onClick={(e) => { e.stopPropagation(); navigate(`/view/${residence.id}`); }}
            >
              <div className={`${styles.iconWrapper} ${styles.electricIcon}`}>
                <Bolt style={{ fontSize: '32px', color: 'var(--color-electricity)' }} />
              </div>
              <div className={styles.metricValue}>{consumption.toFixed(0)}</div>
              <div className={styles.metricLabel}>kWh</div>
            </div>
          )}
        </React.Fragment>
      );
    });
  };

  // Section combinée UC300 (consommation kWh) + ADE_DRY (toggles ON/OFF par canal),
  // utilisée quand le pack a les deux : kWh à gauche en colonne fixe, canaux ADE_DRY
  // en grille 2×2 à droite (ou 1×1, 1×2, 2+1 selon le nombre de canaux disponibles).
  // Cette fonction remplace renderElectricitySection ET la partie ade_dry de
  // renderRelaySection pour les packs concernés (UC300 + ADE_DRY uniquement).
  const renderUC300AdeDryCombo = (usage, screen, deviceData, residence, elecAlert) => {
    if (!usage) return null;
    const hasUC300 = Object.keys(usage).some(k => k.startsWith('uc300_CH'));
    if (!hasUC300) return null;

    // Somme des canaux UC300 (cumul kWh)
    const elecValue = Object.entries(usage)
      .filter(([k]) => k.startsWith('uc300_CH'))
      .reduce((sum, [, v]) => sum + (parseFloat(v) || 0), 0);

    // Canaux ADE_DRY présents
    const letterToNum = { A: 1, B: 2, C: 3, D: 4 };
    const adeChannels = [];
    Object.entries(usage).forEach(([key, val]) => {
      if (!key.startsWith('ade_dry_')) return;
      if (typeof val !== 'object' || !val?.dev_eui) return;
      const letter = val.channel || key.split('_').pop();
      const channelNum = letterToNum[letter];
      if (!channelNum) return;
      const rs = val.relay_state;
      const isOn = rs === 1 || rs === 'ON' || rs === true || rs === 1.0;
      adeChannels.push({
        key,
        channelNum,
        letter,
        label: `Canal ${letter}`,
        dev_eui: val.dev_eui,
        backendState: isOn,
        meta: { brand: 'ade_dry', channel: letter },
      });
    });
    if (adeChannels.length === 0) return null;
    adeChannels.sort((a, b) => a.channelNum - b.channelNum);

    const count = adeChannels.length;
    const gridCols = count === 1 ? '1fr' : 'repeat(2, 1fr)';
    const gridRows = count <= 2 ? '1fr' : 'repeat(2, 1fr)';

    return (
      <div
        className={`${styles.metricSection} ${styles.electricSection}`}
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'stretch',
          justifyContent: 'flex-start',
          gap: '6px',
          padding: '6px',
          position: 'relative',
        }}
      >
        {/* Colonne gauche : kWh, largeur fixe (n'est pas étirée par la valeur) */}
        <div
          onClick={(e) => { e.stopPropagation(); navigate(`/view/${residence.id}`); }}
          style={{
            flex: '0 0 30%',
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '2px',
            cursor: 'pointer',
            borderRight: '1px solid rgba(255,255,255,0.12)',
            paddingRight: '4px',
          }}
        >
          <EvStation style={{ fontSize: isMobile ? '26px' : '32px', color: 'var(--color-electricity)' }} />
          <div className={styles.metricValue} style={{
            fontSize: isMobile ? '1.1rem' : '1.4rem',
            fontWeight: 700,
            lineHeight: 1.1,
          }}>{elecValue.toFixed(0)}</div>
          <div className={styles.metricUnit} style={{ lineHeight: 1.1 }}>kWh</div>
        </div>

        {/* Colonne droite : grille des canaux ADE_DRY (2×2 si 4 canaux) */}
        <div style={{
          flex: '1',
          minWidth: 0,
          display: 'grid',
          gridTemplateColumns: gridCols,
          gridTemplateRows: gridRows,
          gap: '4px',
        }}>
          {adeChannels.map((ch, idx) => {
            const relayKey = `${residence.id}_${ch.key}`;
            const isOn = relayStates[relayKey] !== undefined ? relayStates[relayKey] : ch.backendState;
            // Si 3 canaux : le 3e prend toute la largeur en bas
            const spanFull = count === 3 && idx === 2;
            return (
              <div
                key={ch.key}
                onClick={(e) => handleRelayToggle(e, residence.id, ch.key, ch.dev_eui, ch.meta)}
                style={{
                  gridColumn: spanFull ? '1 / span 2' : 'auto',
                  borderTop: `3px solid ${isOn ? '#27ae60' : '#c0392b'}`,
                  backgroundColor: isOn ? 'rgba(39, 174, 96, 0.08)' : 'rgba(231, 76, 60, 0.08)',
                  borderRadius: '6px',
                  padding: '4px 2px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '2px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
              >
                <div style={{
                  width: isMobile ? '24px' : '32px',
                  height: isMobile ? '24px' : '32px',
                  borderRadius: '50%',
                  backgroundColor: isOn ? '#27ae60' : '#e74c3c',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: isOn
                    ? '0 2px 6px rgba(39, 174, 96, 0.4)'
                    : '0 2px 6px rgba(231, 76, 60, 0.4)',
                }}>
                  <PowerSettingsNew style={{ fontSize: isMobile ? '14px' : '18px', color: 'white' }} />
                </div>
                <span style={{
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  color: 'var(--text-primary)',
                  lineHeight: 1.1,
                }}>{ch.label}</span>
                <span style={{
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  color: isOn ? '#27ae60' : '#e74c3c',
                  lineHeight: 1.1,
                }}>{isOn ? 'ON' : 'OFF'}</span>
              </div>
            );
          })}
        </div>

        {/* Badges d'alerte (sur le bloc kWh) */}
        {elecAlert === "red" && (
          <div style={{ position: "absolute", bottom: "8px", left: "8px", fontSize: "10px", fontWeight: 700, letterSpacing: "0.2px", color: "var(--panel-alert-fg)", backgroundColor: "var(--panel-alert-bg)", animation: "pulse 1.5s infinite", padding: "3px 8px", borderRadius: "4px", boxShadow: "0 1px 2px rgba(0,0,0,0.08)" }}>Alert</div>
        )}
        {elecAlert === "yellow" && (
          <div style={{ position: "absolute", bottom: "6px", left: "6px", fontSize: "18px", lineHeight: 1, filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.15))" }}>⚠️</div>
        )}
      </div>
    );
  };

  // Render section pour les vannes thermo (radiateurs connectés)
  const renderVanneThermoSection = (usage, screen, residence) => {
    // Chercher les données de vanne thermo dans usage_by_type
    const vanneThermoData = [];

    // Trouver le dev_eui du TRV depuis plusieurs sources possibles
    const trvDevice = residence?.devices?.find(d => d.device_brand_id === 66 || d.device_type_id === 8);
    let trvDevEui = trvDevice?.dev_eui;

    // Chercher aussi dans screen.device_data.devices
    if (!trvDevEui && screen?.device_data?.devices) {
      const trvFromScreen = screen.device_data.devices.find(d => d.device_brand_id === 66 || d.device_type_id === 8);
      trvDevEui = trvFromScreen?.dev_eui;
    }

    if (usage) {
      Object.keys(usage).forEach(key => {
        // Détecter par type 'vanne_thermo', 'thermostat' ou par clé contenant 'trv' ou 'vanne'
        if (key === 'vanne_thermo' || key === 'thermostat' || key.includes('trv') || key.includes('vanne')) {
          const data = usage[key];

          // Essayer d'extraire le dev_eui de la clé (ex: trv_8c1f64e270000068)
          let devEuiFromKey = null;
          const keyMatch = key.match(/[_-]?([a-f0-9]{16})$/i);
          if (keyMatch) {
            devEuiFromKey = keyMatch[1];
          }

          // Si c'est un objet, vérifier aussi la propriété dev_eui
          let devEuiFromData = null;
          if (typeof data === 'object' && data.dev_eui) {
            devEuiFromData = data.dev_eui;
          }

          // Priorité: data.dev_eui > clé > devices
          const finalDevEui = devEuiFromData || devEuiFromKey || trvDevEui;

          if (typeof data === 'object') {
            vanneThermoData.push({ key, ...data, dev_eui: finalDevEui });
          } else {
            vanneThermoData.push({ key, temperature: data, dev_eui: finalDevEui });
          }
        }
      });
    }

    if (vanneThermoData.length === 0) return null;

    return vanneThermoData.map((vanneData) => {
      // Appliquer les overrides locaux (mise à jour optimiste après commande)
      const overrideKey = `${residence.id}_trv`;
      const localOverride = trvLocalOverrides[overrideKey] || {};

      // Utiliser ?? au lieu de || pour ne pas traiter 0 comme falsy
      const currentTemp = vanneData.temperature ?? vanneData.current_temperature ?? null;
      const setpointTemp = localOverride.heating_temperature ?? vanneData.heating_temperature ?? vanneData.setpoint ?? 22;

      // Batterie: null = pas de données
      const battery = vanneData.battery ?? null;

      const valveStatus = localOverride.valve_status || vanneData.valve_status || 'auto';

      // Logique ON/OFF basée sur la réception de données
      // - Si aucune donnée reçue (temperature null) → OFF (device pas encore actif)
      // - Dès qu'un payload est reçu (temperature a une valeur) → ON
      const hasReceivedData = currentTemp !== null;
      const isOff = !hasReceivedData || valveStatus === 'closed';
      const isOn = hasReceivedData && valveStatus !== 'closed';
      const isFrost = setpointTemp <= 8;

      // Couleur de la bordure: vert si ON, rouge si OFF, bleu si hors-gel
      const getBorderColor = () => {
        if (isOff) return '#e74c3c'; // Rouge - fermé
        if (isFrost) return '#3498db'; // Bleu - hors-gel
        return '#27ae60'; // Vert - en fonctionnement
      };

      return (
        <div
          key={vanneData.key}
          className={`${styles.metricSection}`}
          style={{
            padding: '12px',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            minWidth: isMobile ? '140px' : '180px',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header avec icône flamme (terracotta en light, orange en dark) */}
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Whatshot style={{ fontSize: '36px', color: 'var(--color-trv)' }} />
          </div>

          {/* Températures */}
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.4rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>
              {currentTemp !== null ? `${currentTemp.toFixed(1)}°C` : '--'}
            </div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
              → {setpointTemp.toFixed(1)}°C
            </div>
          </div>

          {/* Contrôles */}
          <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', flexWrap: 'wrap' }}>
            {/* Indicateur ON/OFF - cliquable pour fermer si ON */}
            <Tooltip title={isOn ? "Fermer la vanne" : "Vanne fermée"}>
              <div
                onClick={(e) => isOn ? handleVanneThermoOpenDialog(e, 'close', vanneData, residence.id) : null}
                style={{
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '10px',
                  fontWeight: 600,
                  cursor: isOn ? 'pointer' : 'default',
                  backgroundColor: isOn ? '#27ae60' : '#e74c3c',
                  color: '#fff',
                  border: `1px solid ${isOn ? '#27ae60' : '#e74c3c'}`,
                }}
              >
                {isOn ? 'ON' : 'OFF'}
              </div>
            </Tooltip>

            {/* Bouton Hors-gel */}
            <Tooltip title="Mode hors-gel (7.5°C)">
              <div
                onClick={(e) => handleVanneThermoOpenDialog(e, 'frost', vanneData, residence.id)}
                style={{
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '10px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  backgroundColor: isFrost && !isOff ? '#3498db' : 'rgba(52,152,219,0.2)',
                  color: isFrost && !isOff ? '#fff' : '#3498db',
                  border: `1px solid ${isFrost && !isOff ? '#3498db' : 'rgba(52,152,219,0.3)'}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '2px',
                }}
              >
                <AcUnit style={{ fontSize: '12px' }} />
              </div>
            </Tooltip>

            {/* Bouton Auto/Température */}
            <Tooltip title="Régler la température">
              <div
                onClick={(e) => handleVanneThermoOpenDialog(e, 'temperature', vanneData, residence.id)}
                style={{
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '10px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  backgroundColor: !isOff && !isFrost ? '#27ae60' : 'rgba(39,174,96,0.2)',
                  color: !isOff && !isFrost ? '#fff' : '#27ae60',
                  border: `1px solid ${!isOff && !isFrost ? '#27ae60' : 'rgba(39,174,96,0.3)'}`,
                }}
              >
                🔥 Auto
              </div>
            </Tooltip>
          </div>

          {/* Lien historique */}
          <div
            onClick={(e) => { e.stopPropagation(); navigate(`/view/${residence.id}`); }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
              fontSize: '10px',
              color: '#6b9dad',
              cursor: 'pointer',
              marginTop: '4px',
            }}
          >
            <History style={{ fontSize: '14px' }} />
            Historique
          </div>
        </div>
      );
    });
  };


  // === FONCTIONS DE RENDU VUE LISTE ===

  // Calculer les coûts eau et électricité
  const calculateCosts = (screen, deviceData) => {
    const waterLiter = screen?.water_liter || 0;
    const waterM3 = waterLiter / 1000; // Convertir litres en m³
    const costPerM3 = currentEstablishmentData?.cost_per_liter || 0; // C'est en fait €/m³
    const waterCost = waterM3 * costPerM3;

    // Électricité - vérifier UC300 / Dragino CS01-LB / ATIM MR4 (multicanal) ou standard
    const hasUC300 = deviceData?.usage_by_type && Object.keys(deviceData.usage_by_type).some(key => key.startsWith("uc300_CH"));
    const hasDragino = deviceData?.usage_by_type && Object.keys(deviceData.usage_by_type).some(key => key.startsWith("dragino_CH"));
    const hasAtimMR4 = deviceData?.usage_by_type && Object.keys(deviceData.usage_by_type).some(key => key.startsWith("atim_mr4_CH"));
    const hasMultiChannelElec = hasUC300 || hasDragino || hasAtimMR4;
    const elecKwh = hasMultiChannelElec
      ? Object.entries(deviceData.usage_by_type || {})
          .filter(([key]) => key.startsWith("uc300_CH") || key.startsWith("dragino_CH") || key.startsWith("atim_mr4_CH"))
          .reduce((sum, [, value]) => sum + (parseFloat(value) || 0), 0)
      : (deviceData?.usage_by_type?.electric || screen?.electricity_kwh || 0);
    const costPerKwh = currentEstablishmentData?.cost_per_kwh || 0;
    const elecCost = elecKwh * costPerKwh;

    const totalCost = waterCost + elecCost;

    return {
      waterCost: waterCost > 0 ? waterCost.toFixed(2) : null,
      elecCost: elecCost > 0 ? elecCost.toFixed(2) : null,
      totalCost: totalCost > 0 ? totalCost.toFixed(2) : null,
      hasWater: deviceData?.usage_by_type ? "water" in deviceData.usage_by_type : waterLiter > 0,
      hasElec: hasMultiChannelElec || (deviceData?.usage_by_type ? "electric" in deviceData.usage_by_type : false),
    };
  };

  // Badge métrique compact pour vue liste
  const renderListBadge = (icon, value, unit, color, alertColor = null) => (
    <div className={styles.listBadge} style={{
      borderLeft: `3px solid ${color}`,
      backgroundColor: alertColor === 'red' ? 'rgba(254, 62, 38, 0.2)' :
                       alertColor === 'yellow' ? 'rgba(254, 221, 55, 0.2)' :
                       'rgba(255,255,255,0.05)',
    }}>
      <span className={styles.listBadgeIcon}>{icon}</span>
      <span className={styles.listBadgeValue}>{value}</span>
      <span className={styles.listBadgeUnit}>{unit}</span>
      {alertColor && (
        <span className={`${styles.listAlertDot} ${alertColor === 'red' ? styles.alertRed : styles.alertYellow}`} />
      )}
    </div>
  );

  // Rendu eau pour vue liste
  const renderWaterListItem = (screen, deviceData, waterAlert) => {
    const waterValue = screen?.water_liter || 0;
    if (deviceData?.usage_by_type && !("water" in deviceData.usage_by_type)) return null;
    return renderListBadge(<WaterDrop style={{ fontSize: '18px', color: '#4fc3f7' }} />, waterValue.toFixed(0), 'L', '#4fc3f7', waterAlert);
  };

  // Rendu électricité pour vue liste
  const renderElectricityListItem = (screen, deviceData, elecAlert) => {
    const hasUC300 = deviceData?.usage_by_type && Object.keys(deviceData.usage_by_type).some(key => key.startsWith("uc300_CH"));
    const hasDragino = deviceData?.usage_by_type && Object.keys(deviceData.usage_by_type).some(key => key.startsWith("dragino_CH"));
    const hasAtimMR4 = deviceData?.usage_by_type && Object.keys(deviceData.usage_by_type).some(key => key.startsWith("atim_mr4_CH"));
    const hasMultiChannelElec = hasUC300 || hasDragino || hasAtimMR4;
    const elecValue = hasMultiChannelElec
      ? Object.entries(deviceData.usage_by_type || {})
          .filter(([key]) => key.startsWith("uc300_CH") || key.startsWith("dragino_CH") || key.startsWith("atim_mr4_CH"))
          .reduce((sum, [, value]) => sum + (parseFloat(value) || 0), 0)
      : (deviceData?.usage_by_type?.electric || screen?.electricity_kwh || 0);
    if (!hasMultiChannelElec && deviceData?.usage_by_type && !("electric" in deviceData.usage_by_type)) return null;
    const icon = hasUC300
      ? <EvStation style={{ fontSize: '18px', color: '#ffa726' }} />
      : <Bolt style={{ fontSize: '18px', color: '#ffd54f' }} />;
    return renderListBadge(icon, elecValue.toFixed(0), 'kWh', '#ffd54f', elecAlert);
  };

  // Rendu température pour vue liste
  const renderTemperatureListItems = (usage, screen) => {
    const items = [];

    // Détection AM103 par device_brand_id (35-38). SenseCap (27-29) garde le flocon comme une chambre froide.
    const devicesList = screen?.device_data?.devices || [];
    const isAM103 = devicesList.some(d => [35, 36, 37, 38].includes(d.device_brand_id));
    const tempIcon = isAM103 ? '🌡️' : '❄️';
    const tempColor = isAM103 ? '#4fc3f7' : '#99DCFF';

    // Température simple
    if (usage?.temperature) {
      if (isAM103 && usage.temperature > 100) {
        items.push({ key: 'co2', value: usage.temperature, icon: '🌬️', unit: 'ppm', color: '#4caf50' });
      } else if (!isAM103) {
        items.push({ key: 'temperature', value: usage.temperature, icon: tempIcon, unit: '°C', color: tempColor });
      }
    }

    // Canaux température
    ['temperature_CH1', 'temperature_CH2'].forEach(key => {
      if (usage?.[key]) {
        const channelLabel = key.split('_')[1];
        items.push({
          key,
          value: usage[key],
          icon: tempIcon,
          unit: '°C',
          color: tempColor,
          label: channelLabel
        });
      }
    });

    // CH3 : AM103 = humidité ; SenseCap 3 canaux = 3e température
    if (usage?.humidity) {
      items.push({ key: 'humidity', value: usage.humidity, icon: '💧', unit: '%', color: '#00bcd4' });
    } else if (usage?.temperature_CH3) {
      if (isAM103) {
        items.push({ key: 'humidity', value: usage.temperature_CH3, icon: '💧', unit: '%', color: '#00bcd4' });
      } else {
        items.push({ key: 'temperature_CH3', value: usage.temperature_CH3, icon: tempIcon, unit: '°C', color: tempColor, label: 'CH3' });
      }
    }

    // CO2
    if (usage?.co2) {
      items.push({ key: 'co2', value: usage.co2, icon: '🌬️', unit: 'ppm', color: '#4caf50' });
    } else if (usage?.temperature_CH4) {
      items.push({ key: 'co2', value: usage.temperature_CH4, icon: '🌬️', unit: 'ppm', color: '#4caf50' });
    }

    return items.map(({ key, value, icon, unit, color, label }) => {
      const channelColor = screen?.device_data?.colors?.[key] || "green";
      const alertColor = channelColor === 'red' ? 'red' : channelColor === 'yellow' ? 'yellow' : null;
      return (
        <React.Fragment key={key}>
          {renderListBadge(
            <span style={{ fontSize: '16px' }}>{icon}</span>,
            `${(Math.round(value * 100) / 100)}${label ? ` ${label}` : ''}`,
            unit,
            color,
            alertColor
          )}
        </React.Fragment>
      );
    });
  };

  // Rendu relais pour vue liste
  const renderRelayListItem = (usage, screen, residence) => {
    let relayData = [];

    if (usage) {
      Object.keys(usage).forEach(key => {
        if (key.startsWith('relay_') || key.startsWith('smart_plug_')) {
          relayData.push({ key, consumption: usage[key] || 0 });
          return;
        }
        if (key === 'switch') {
          const switchData = usage[key];
          if (typeof switchData === 'object' && switchData.dev_eui) {
            const relayState = switchData.relay_state;
            const isRelayOn = relayState === 1 || relayState === 'ON' || relayState === true || relayState === 1.0;
            relayData.push({ key, consumption: 0, dev_eui: switchData.dev_eui, backendState: isRelayOn });
          } else {
            relayData.push({ key, consumption: switchData || 0, backendState: switchData >= 1 });
          }
        }
      });
    }

    if (relayData.length === 0) return null;

    return relayData.map(({ key, dev_eui, backendState }) => {
      const relayKey = `${residence.id}_${key}`;
      const isOn = relayStates[relayKey] !== undefined ? relayStates[relayKey] : (backendState !== undefined ? backendState : mclimateRelayState);

      return (
        <div
          key={key}
          className={styles.listBadge}
          style={{
            borderLeft: `3px solid ${isOn ? '#27ae60' : '#e74c3c'}`,
            backgroundColor: isOn ? 'rgba(39, 174, 96, 0.2)' : 'rgba(231, 76, 60, 0.2)',
            cursor: 'pointer',
          }}
          onClick={(e) => handleRelayToggle(e, residence.id, key, dev_eui)}
        >
          <PowerSettingsNew style={{ fontSize: '18px', color: isOn ? '#27ae60' : '#e74c3c' }} />
          <span className={styles.listBadgeValue} style={{ color: isOn ? '#27ae60' : '#e74c3c', fontWeight: 600 }}>
            {isOn ? 'ON' : 'OFF'}
          </span>
        </div>
      );
    });
  };

  // Rendu vanne thermo pour vue liste
  const renderVanneThermoListItem = (usage, screen, residence) => {
    const vanneThermoData = [];
    const trvDevice = residence?.devices?.find(d => d.device_brand_id === 66 || d.device_type_id === 8);
    let trvDevEui = trvDevice?.dev_eui;

    if (!trvDevEui && screen?.device_data?.devices) {
      const trvFromScreen = screen.device_data.devices.find(d => d.device_brand_id === 66 || d.device_type_id === 8);
      trvDevEui = trvFromScreen?.dev_eui;
    }

    if (usage) {
      Object.keys(usage).forEach(key => {
        if (key === 'vanne_thermo' || key === 'thermostat' || key.includes('trv') || key.includes('vanne')) {
          const data = usage[key];
          let devEuiFromKey = null;
          const keyMatch = key.match(/[_-]?([a-f0-9]{16})$/i);
          if (keyMatch) devEuiFromKey = keyMatch[1];

          let devEuiFromData = null;
          if (typeof data === 'object' && data.dev_eui) devEuiFromData = data.dev_eui;

          const finalDevEui = devEuiFromData || devEuiFromKey || trvDevEui;

          if (typeof data === 'object') {
            vanneThermoData.push({ key, ...data, dev_eui: finalDevEui });
          } else {
            vanneThermoData.push({ key, temperature: data, dev_eui: finalDevEui });
          }
        }
      });
    }

    if (vanneThermoData.length === 0) return null;

    return vanneThermoData.map((vanneData) => {
      const overrideKey = `${residence.id}_trv`;
      const localOverride = trvLocalOverrides[overrideKey] || {};
      const currentTemp = vanneData.temperature ?? vanneData.current_temperature ?? null;
      const setpointTemp = localOverride.heating_temperature ?? vanneData.heating_temperature ?? vanneData.setpoint ?? 22;
      const valveStatus = localOverride.valve_status || vanneData.valve_status || 'auto';
      const hasReceivedData = currentTemp !== null;
      const isOff = !hasReceivedData || valveStatus === 'closed';
      const isFrost = setpointTemp <= 8;

      return (
        <div
          key={vanneData.key}
          className={styles.listBadge}
          style={{
            borderLeft: `3px solid #F97316`,
            backgroundColor: isOff ? 'rgba(231, 76, 60, 0.15)' : isFrost ? 'rgba(52, 152, 219, 0.15)' : 'rgba(249, 115, 22, 0.15)',
            cursor: 'pointer',
          }}
          onClick={(e) => handleVanneThermoOpenDialog(e, 'temperature', vanneData, residence.id)}
        >
          <Whatshot style={{ fontSize: '18px', color: '#F97316' }} />
          <span className={styles.listBadgeValue}>
            {currentTemp !== null ? `${currentTemp.toFixed(1)}°` : '--'}
          </span>
          <span className={styles.listBadgeUnit} style={{ color: '#9ca3af' }}>
            → {setpointTemp.toFixed(1)}°
          </span>
          {isOff && <span style={{ marginLeft: '4px', fontSize: '10px', color: '#e74c3c', fontWeight: 600 }}>OFF</span>}
          {isFrost && !isOff && <AcUnit style={{ fontSize: '14px', color: '#3498db', marginLeft: '4px' }} />}
        </div>
      );
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", flexWrap: "wrap", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box>
              <Typography sx={{ display: { xs: "none", sm: "block" } }} variant={isMobile ? "h6" : "h4"} style={{ fontWeight: 600 }} className={styles.title}>
                {currentEstablishmentData?.name || "Établissement"}
              </Typography>
              <Typography sx={{ display: { xs: "none", sm: "block" } }} className={styles.subtitle} style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>
                Installations ({filteredResidences?.length || 0})
              </Typography>
            </Box>
            {/* Toggle vue cartes/liste — style aligné sur l'item actif
                de la sidebar "Tableau de bord" : fond sage pâle, icône
                sage foncée, hover sage un ton plus foncé. */}
            {!isSmallMobile && (
              <Box sx={{
                display: "flex",
                gap: 1,
              }}>
                <Tooltip title="Vue cartes">
                  <Box
                    onClick={() => { setViewMode('cards'); localStorage.setItem("dashboardViewMode", 'cards'); }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 36,
                      height: 36,
                      borderRadius: "8px",
                      cursor: "pointer",
                      transition: "background-color 0.2s ease, border-color 0.2s ease",
                      color: viewMode === 'cards' ? 'var(--menu-selected-fg)' : 'var(--text-secondary)',
                      backgroundColor: viewMode === 'cards' ? 'var(--menu-selected-bg)' : 'var(--bg-card, transparent)',
                      border: viewMode === 'cards' ? '1px solid var(--menu-selected-bg)' : '1px solid var(--border-light)',
                      "&:hover": {
                        backgroundColor: viewMode === 'cards' ? 'var(--menu-selected-hover-bg)' : 'var(--bg-hover)',
                        borderColor: viewMode === 'cards' ? 'var(--menu-selected-hover-bg)' : 'var(--border-light)',
                      },
                    }}
                  >
                    <GridView fontSize="small" />
                  </Box>
                </Tooltip>
                <Tooltip title="Vue liste">
                  <Box
                    onClick={() => { setViewMode('list'); localStorage.setItem("dashboardViewMode", 'list'); }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 36,
                      height: 36,
                      borderRadius: "8px",
                      cursor: "pointer",
                      transition: "background-color 0.2s ease, border-color 0.2s ease",
                      color: viewMode === 'list' ? 'var(--menu-selected-fg)' : 'var(--text-secondary)',
                      backgroundColor: viewMode === 'list' ? 'var(--menu-selected-bg)' : 'var(--bg-card, transparent)',
                      border: viewMode === 'list' ? '1px solid var(--menu-selected-bg)' : '1px solid var(--border-light)',
                      "&:hover": {
                        backgroundColor: viewMode === 'list' ? 'var(--menu-selected-hover-bg)' : 'var(--bg-hover)',
                        borderColor: viewMode === 'list' ? 'var(--menu-selected-hover-bg)' : 'var(--border-light)',
                      },
                    }}
                  >
                    <ViewList fontSize="small" />
                  </Box>
                </Tooltip>

                {/* Tri par conso — visibles uniquement en vue liste.
                    Clic : active le tri descendant sur la métrique.
                    Re-clic : désactive (retour au tri par défaut alertes+nom). */}
                {viewMode === 'list' && (
                  <>
                    <Box sx={{ width: '1px', alignSelf: 'stretch', backgroundColor: 'var(--border-light)', mx: 0.5 }} />
                    <Tooltip title={listSortBy === 'water' ? "Tri eau actif — cliquer pour désactiver" : "Trier par conso eau (décroissante)"}>
                      <Box
                        onClick={() => toggleListSort('water')}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 36,
                          height: 36,
                          borderRadius: "8px",
                          cursor: "pointer",
                          transition: "background-color 0.2s ease, border-color 0.2s ease",
                          color: listSortBy === 'water' ? 'var(--menu-selected-fg)' : 'var(--color-water)',
                          backgroundColor: listSortBy === 'water' ? 'var(--menu-selected-bg)' : 'var(--bg-card, transparent)',
                          border: listSortBy === 'water' ? '1px solid var(--menu-selected-bg)' : '1px solid var(--border-light)',
                          "&:hover": {
                            backgroundColor: listSortBy === 'water' ? 'var(--menu-selected-hover-bg)' : 'var(--bg-hover)',
                            borderColor: listSortBy === 'water' ? 'var(--menu-selected-hover-bg)' : 'var(--border-light)',
                          },
                        }}
                      >
                        <WaterDrop fontSize="small" />
                      </Box>
                    </Tooltip>
                    <Tooltip title={listSortBy === 'electric' ? "Tri élec actif — cliquer pour désactiver" : "Trier par conso électrique (décroissante)"}>
                      <Box
                        onClick={() => toggleListSort('electric')}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 36,
                          height: 36,
                          borderRadius: "8px",
                          cursor: "pointer",
                          transition: "background-color 0.2s ease, border-color 0.2s ease",
                          color: listSortBy === 'electric' ? 'var(--menu-selected-fg)' : 'var(--color-electricity)',
                          backgroundColor: listSortBy === 'electric' ? 'var(--menu-selected-bg)' : 'var(--bg-card, transparent)',
                          border: listSortBy === 'electric' ? '1px solid var(--menu-selected-bg)' : '1px solid var(--border-light)',
                          "&:hover": {
                            backgroundColor: listSortBy === 'electric' ? 'var(--menu-selected-hover-bg)' : 'var(--bg-hover)',
                            borderColor: listSortBy === 'electric' ? 'var(--menu-selected-hover-bg)' : 'var(--border-light)',
                          },
                        }}
                      >
                        <Bolt fontSize="small" />
                      </Box>
                    </Tooltip>
                  </>
                )}
              </Box>
            )}
          </Box>

          {/* Sélecteur de zone - Dropdown sur mobile, pills drag & drop sur desktop */}
          {availableZones.length > 0 && isMobile && (
            <FormControl
              size="small"
              sx={{
                width: "100%",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "20px",
                  backgroundColor: "rgba(255,255,255,0.05)",
                  color: "white",
                  "& fieldset": { borderColor: "rgba(255,255,255,0.2)" },
                  "&:hover fieldset": { borderColor: "rgba(255,255,255,0.4)" },
                  "&.Mui-focused fieldset": { borderColor: "#78a882" },
                },
                "& .MuiSelect-icon": { color: "rgba(255,255,255,0.7)" },
                "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.7)" },
                "& .MuiInputLabel-root.Mui-focused": { color: "#78a882" },
              }}
            >
              <InputLabel id="zone-select-label">Filtrer par zone</InputLabel>
              <Select
                labelId="zone-select-label"
                label="Filtrer par zone"
                value={(selectedZone || localSelectedZone) ?? "all"}
                onChange={(e) => handleZoneChange({ target: { value: e.target.value } })}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: "#1a1f1b",
                      color: "white",
                      "& .MuiMenuItem-root:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                      "& .Mui-selected": { backgroundColor: "rgba(120,168,130,0.3) !important" },
                    },
                  },
                }}
              >
                <MenuItem value="all">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                    <Typography sx={{ flex: 1, fontSize: "0.95rem", fontWeight: 500 }}>Toutes</Typography>
                    <Box sx={{ backgroundColor: "rgba(255,255,255,0.15)", borderRadius: "10px", px: 1, py: 0.25, minWidth: 28, textAlign: "center" }}>
                      <Typography sx={{ fontSize: "0.8rem", fontWeight: 600 }}>
                        {residences?.filter(r => {
                          const currentEst = selectedEstablishment || localEstablishment;
                          return r.establishment_id === (typeof currentEst === 'object' ? currentEst?.id : currentEst) && r.active !== 0;
                        }).length || 0}
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
                {availableZones.map((zone) => (
                  <MenuItem key={zone.id} value={zone.id}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: zone.color || "#c4a574" }} />
                      <Typography sx={{ flex: 1, fontSize: "0.95rem" }}>{zone.name}</Typography>
                      <Box sx={{ backgroundColor: "rgba(255,255,255,0.15)", borderRadius: "10px", px: 1, py: 0.25, minWidth: 28, textAlign: "center" }}>
                        <Typography sx={{ fontSize: "0.8rem", fontWeight: 600 }}>{zone.count}</Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Sélecteur de zone - Pills minimalistes style Notion (desktop uniquement) */}
          {availableZones.length > 0 && !isMobile && (
            <Box sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1.5,
              alignItems: "center",
            }}>
              {/* Pill "Toutes" */}
              <Box
                onClick={() => handleZoneChange({ target: { value: "all" } })}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 2,
                  py: 1,
                  borderRadius: "999px",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  backgroundColor: (!selectedZone && !localSelectedZone) ? "var(--menu-selected-bg)" : "var(--bg-card, transparent)",
                  border: (!selectedZone && !localSelectedZone) ? "1px solid var(--menu-selected-bg)" : "1px solid var(--border-light)",
                  "&:hover": {
                    backgroundColor: (!selectedZone && !localSelectedZone) ? "var(--menu-selected-hover-bg)" : "var(--bg-hover)",
                  }
                }}
              >
                <Typography sx={{
                  fontSize: "0.75rem",
                  fontWeight: (!selectedZone && !localSelectedZone) ? 700 : 600,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  color: (!selectedZone && !localSelectedZone) ? "var(--menu-selected-fg)" : "var(--text-secondary)",
                }}>
                  Toutes
                </Typography>
                <Box sx={{
                  backgroundColor: (!selectedZone && !localSelectedZone) ? "rgba(44,58,40,0.08)" : "var(--badge-bg)",
                  borderRadius: "999px",
                  px: 0.75,
                  py: 0.125,
                  minWidth: 20,
                  textAlign: "center"
                }}>
                  <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: (!selectedZone && !localSelectedZone) ? "var(--menu-selected-fg)" : "var(--text-secondary)" }}>
                    {residences?.filter(r => {
                      const currentEst = selectedEstablishment || localEstablishment;
                      return r.establishment_id === (typeof currentEst === 'object' ? currentEst?.id : currentEst) && r.active !== 0;
                    }).length || 0}
                  </Typography>
                </Box>
              </Box>

              {/* Pills des zones (drag & drop pour réorganiser) */}
              <DndContext
                sensors={dndSensors}
                collisionDetection={closestCenter}
                onDragEnd={handleZonesDragEnd}
              >
                <SortableContext
                  items={availableZones.map(z => z.id)}
                  strategy={horizontalListSortingStrategy}
                >
                  {availableZones.map((zone) => {
                    const isActive = (selectedZone || localSelectedZone) === zone.id;
                    return (
                      <SortableZonePill
                        key={zone.id}
                        zone={zone}
                        isActive={isActive}
                        onClick={() => handleZoneChange({ target: { value: zone.id } })}
                      />
                    );
                  })}
                </SortableContext>
              </DndContext>
            </Box>
          )}
        </Box>
      </div>

      {filteredResidences.length === 0 ? (
          <Typography variant="h5" sx={{ fontSize: "1.5rem", fontWeight: 500, textAlign: "center", padding: "2rem", color: "white" }}>{"🔄 Chargement des données..."}</Typography>
      ) : viewMode === 'cards' ? (
        // === VUE CARTES ===
        <div className={styles.grid}>
          {filteredResidences.map((residence) => {
            const screen = screens?.find(s => s.pack_id === residence.pack_id);
            const deviceData = screen?.device_data || {};
            const usage = deviceData.usage_by_type || {};
            const establishment = establishmentsFromStore.find(e => e.id === residence.establishment_id);
            const { waterAlert, elecAlert, tempAlert } = getResidenceAlerts(screen, deviceData, residence, establishment);

            return (
              <div
                key={residence.id}
                className={styles.card} style={{ position: "relative", overflow: "hidden" }}
                onClick={() => handleCardClick(residence)}
              >
                {(() => {
                  const railColor = getBorderColor(waterAlert, elecAlert, tempAlert);
                  return railColor ? (
                    <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "3px", backgroundColor: railColor, zIndex: 1 }}></div>
                  ) : null;
                })()}
                <div className={styles.cardHeader}>
                  <h3 className={styles.cardTitle}>{residence.name}</h3>
                </div>

                <div className={styles.cardBody}>
                  {(() => {
                    // Quand le pack a un UC300 + un ADE_DRY, on les rend ensemble
                    // dans une section combinée (kWh à gauche, toggles 2×2 à droite),
                    // et on saute le rendu indépendant de chacun pour éviter le doublon.
                    const hasUC300 = Object.keys(usage).some(k => k.startsWith('uc300_CH'));
                    const hasAdeDry = Object.keys(usage).some(k => k.startsWith('ade_dry_'));
                    const showCombo = hasUC300 && hasAdeDry;
                    return (
                      <>
                        {renderWaterSection(screen, deviceData, waterAlert)}
                        {showCombo
                          ? renderUC300AdeDryCombo(usage, screen, deviceData, residence, elecAlert)
                          : renderElectricitySection(screen, deviceData, elecAlert)}
                        {renderTemperatureSection(usage, screen)}
                        {renderRelaySection(usage, screen, residence, showCombo)}
                        {renderVanneThermoSection(usage, screen, residence)}
                      </>
                    );
                  })()}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        // === VUE LISTE ===
        <div className={styles.listContainer}>
          {filteredResidences.map((residence) => {
            const screen = screens?.find(s => s.pack_id === residence.pack_id);
            const deviceData = screen?.device_data || {};
            const usage = deviceData.usage_by_type || {};
            const establishment = establishmentsFromStore.find(e => e.id === residence.establishment_id);
            const { waterAlert, elecAlert, tempAlert } = getResidenceAlerts(screen, deviceData, residence, establishment);
            const borderColor = getBorderColor(waterAlert, elecAlert, tempAlert);

            return (
              <div
                key={residence.id}
                className={styles.listRow}
                style={{ borderLeftColor: borderColor }}
                onClick={() => handleCardClick(residence)}
              >
                {/* Nom de la résidence */}
                <div className={styles.listRowName}>
                  {residence.name}
                </div>

                {/* Badges des métriques */}
                <div className={styles.listRowMetrics}>
                  {renderWaterListItem(screen, deviceData, waterAlert)}
                  {renderElectricityListItem(screen, deviceData, elecAlert)}
                  {renderTemperatureListItems(usage, screen)}
                  {renderRelayListItem(usage, screen, residence)}
                  {renderVanneThermoListItem(usage, screen, residence)}
                </div>

                {/* Coûts à droite */}
                {(() => {
                  const costs = calculateCosts(screen, deviceData);
                  const hasCosts = costs.waterCost || costs.elecCost;
                  return (
                    <div className={styles.listRowRight}>
                      {hasCosts ? (
                        <div className={styles.costsBadge}>
                          <Euro style={{ fontSize: '16px', color: '#10b981' }} />
                          <span className={styles.costsValue}>{costs.totalCost} €</span>
                        </div>
                      ) : (
                        <div className={styles.costsBadge} style={{ opacity: 0.4 }}>
                          <Euro style={{ fontSize: '16px', color: '#6b7280' }} />
                          <span className={styles.costsValue} style={{ color: '#6b7280' }}>--</span>
                        </div>
                      )}
                      <ChevronRight className={styles.listRowChevron} />
                    </div>
                  );
                })()}
              </div>
            );
          })}
        </div>
      )}

      {/* Contrôles de zoom */}
      {!isMobile && (
      <Box sx={{
        position: 'fixed',
        bottom: 20,
        right: 20,
        zIndex: 1000,
        display: 'flex',
        gap: 1,
        backgroundColor: 'rgba(0,0,0,0.7)',
        borderRadius: 2,
        padding: 1
      }}>
        <Tooltip title="Réduire">
          <IconButton
            onClick={decreaseSize}
            disabled={cardSize === CARD_SIZES[0].value}
            sx={{ color: 'white' }}
          >
            <ZoomOut />
          </IconButton>
        </Tooltip>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          color: 'white',
          fontSize: '12px',
          minWidth: '60px',
          textAlign: 'center'
        }}>
          {CARD_SIZES.find(s => s.value === cardSize)?.label}
        </Box>
        <Tooltip title="Agrandir">
          <IconButton
            onClick={increaseSize}
            disabled={cardSize === CARD_SIZES[CARD_SIZES.length - 1].value}
            sx={{ color: 'white' }}
          >
            <ZoomIn />
          </IconButton>
        </Tooltip>
      </Box>
      )}

      {/* Dialog de confirmation vanne thermo */}
      <Dialog
        open={vanneThermoDialog.open}
        onClose={handleVanneThermoCloseDialog}
        PaperProps={{
          sx: {
            backgroundColor: '#1a1a2e',
            color: 'white',
            borderRadius: 2,
            minWidth: 320,
          }
        }}
      >
        <DialogTitle sx={{
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}>
          <Whatshot sx={{ color: '#e67e22' }} />
          {vanneThermoDialog.type === 'temperature' && 'Régler la température'}
          {vanneThermoDialog.type === 'close' && 'Fermer la vanne'}
          {vanneThermoDialog.type === 'frost' && 'Mode hors-gel'}
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          {vanneThermoSuccess ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography sx={{ color: '#27ae60', fontSize: '1.2rem', fontWeight: 600 }}>
                ✅ Commande envoyée !
              </Typography>
              <Typography sx={{ color: '#9ca3af', mt: 1, fontSize: '0.9rem' }}>
                La vanne va recevoir l'instruction.
              </Typography>
            </Box>
          ) : (
            <>
              {/* Mode température - avec slider */}
              {vanneThermoDialog.type === 'temperature' && (
                <Box sx={{ py: 2 }}>
                  <Typography sx={{ mb: 2, color: '#9ca3af' }}>
                    Température actuelle : <strong style={{ color: '#fff' }}>{vanneThermoDialog.currentValue}°C</strong>
                  </Typography>

                  <Typography sx={{ mb: 1 }}>
                    Nouvelle température :
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <AcUnit sx={{ color: '#3498db' }} />
                    <Slider
                      value={vanneThermoDialog.newValue}
                      onChange={(e, value) => setVanneThermoDialog(prev => ({ ...prev, newValue: value }))}
                      min={7.5}
                      max={28.5}
                      step={0.5}
                      marks={[
                        { value: 7.5, label: '7.5°' },
                        { value: 15, label: '15°' },
                        { value: 22, label: '22°' },
                        { value: 28.5, label: '28.5°' },
                      ]}
                      sx={{
                        color: '#e67e22',
                        '& .MuiSlider-markLabel': { color: '#9ca3af', fontSize: '0.75rem' },
                        '& .MuiSlider-thumb': { backgroundColor: '#e67e22' },
                        '& .MuiSlider-track': { backgroundColor: '#e67e22' },
                        '& .MuiSlider-rail': { backgroundColor: 'rgba(255,255,255,0.2)' },
                      }}
                    />
                    <Whatshot sx={{ color: '#e74c3c' }} />
                  </Box>

                  <Typography sx={{ textAlign: 'center', fontSize: '2rem', fontWeight: 'bold', color: '#e67e22' }}>
                    {vanneThermoDialog.newValue}°C
                  </Typography>
                </Box>
              )}

              {/* Mode fermeture */}
              {vanneThermoDialog.type === 'close' && (
                <Box sx={{ py: 2, textAlign: 'center' }}>
                  <Typography sx={{ mb: 2 }}>
                    Voulez-vous <strong style={{ color: '#e74c3c' }}>fermer complètement</strong> la vanne ?
                  </Typography>
                  <Typography sx={{ color: '#9ca3af', fontSize: '0.9rem' }}>
                    Le radiateur ne chauffera plus jusqu'à nouvelle commande.
                  </Typography>
                </Box>
              )}

              {/* Mode hors-gel */}
              {vanneThermoDialog.type === 'frost' && (
                <Box sx={{ py: 2, textAlign: 'center' }}>
                  <AcUnit sx={{ fontSize: 48, color: '#3498db', mb: 2 }} />
                  <Typography sx={{ mb: 2 }}>
                    Activer le <strong style={{ color: '#3498db' }}>mode hors-gel</strong> ?
                  </Typography>
                  <Typography sx={{ color: '#9ca3af', fontSize: '0.9rem' }}>
                    La température sera maintenue à <strong>7.5°C</strong> minimum pour éviter le gel des canalisations.
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ borderTop: '1px solid rgba(255,255,255,0.1)', p: 2 }}>
          {!vanneThermoSuccess && (
            <>
              <Button
                onClick={handleVanneThermoCloseDialog}
                sx={{ color: '#9ca3af' }}
              >
                Annuler
              </Button>
              <Button
                variant="contained"
                disabled={vanneThermoLoading}
                onClick={() => {
                  if (vanneThermoDialog.type === 'temperature') {
                    handleVanneThermoCommand('set_temperature', vanneThermoDialog.newValue);
                  } else if (vanneThermoDialog.type === 'close') {
                    handleVanneThermoCommand('close_valve');
                  } else if (vanneThermoDialog.type === 'frost') {
                    handleVanneThermoCommand('frost_protection');
                  }
                }}
                sx={{
                  backgroundColor: vanneThermoDialog.type === 'close' ? '#e74c3c'
                    : vanneThermoDialog.type === 'frost' ? '#3498db'
                    : '#e67e22',
                  '&:hover': {
                    backgroundColor: vanneThermoDialog.type === 'close' ? '#c0392b'
                      : vanneThermoDialog.type === 'frost' ? '#2980b9'
                      : '#d35400',
                  }
                }}
              >
                {vanneThermoLoading ? (
                  <CircularProgress size={20} sx={{ color: 'white' }} />
                ) : (
                  'Confirmer'
                )}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
}
